import { Card } from "../../../../../components/ui/common/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { ClimateImpactFormatter } from "../../../ingredientList/services/ClimateImpactFormatter";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";

interface UnitDetailsTableProps {
  ingredientDetails: IngredientRowViewModel | undefined;
  isUserSupplier: boolean;
}

const CARD_HEADER_CLASS = "tw-text-sm tw-font-semibold tw-text-primary";

/**
 * "Unit Details" card — a single-row table (ingredients only ever carry one
 * set of unit scalars: `unit_size`/`unit_type`/`ean_bar_code`, unlike
 * products' `sales_units` array) surfacing the same fields the ingredient
 * list table shows per row, plus climate impact per kg. Uses the shared
 * `Table` primitives' own default styling (plain muted header, thin row
 * borders) rather than the primary-tinted `INGREDIENT_DETAILS_TABLE_*`
 * classes — the card itself already supplies the border/rounding, so
 * wrapping the table in a second bordered/rounded box (as those constants
 * do) double-boxed it and clipped the header row's corners against the
 * mock, which shows a plain flush table.
 */
const UnitDetailsTable = ({ ingredientDetails, isUserSupplier }: UnitDetailsTableProps) => {
  const details = ingredientDetails?.supplierIngredientDetails;

  return (
    <Card className="tw-gap-0 tw-overflow-hidden tw-rounded-lg tw-py-0">
      <div className="tw-border-b tw-border-primary/20 tw-px-5 tw-py-3">
        <p className={CARD_HEADER_CLASS}>Unit Details</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw-pl-5">Ingredient Name</TableHead>
            <TableHead className="tw-text-right">Unit Size</TableHead>
            <TableHead>Unit Type</TableHead>
            <TableHead>EAN Barcode</TableHead>
            <TableHead className="tw-pr-5 tw-text-right">kgCO2eq/kg</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="tw-pl-5 tw-font-medium">
              {ingredientDetails?.ingredient_name || "-"}
            </TableCell>
            <TableCell className="tw-text-right tw-font-mono">{details?.unit_size ?? "-"}</TableCell>
            <TableCell className="tw-font-mono">{details?.unit_type || "-"}</TableCell>
            <TableCell className="tw-font-mono tw-text-muted-foreground">
              {details?.ean_bar_code || "-"}
            </TableCell>
            <TableCell className="tw-pr-5 tw-text-right tw-font-mono">
              {ingredientDetails ? ClimateImpactFormatter.format(ingredientDetails, isUserSupplier) : "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
};

export default UnitDetailsTable;
